import { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import api from "../lib/api";
import { toast } from "sonner";
import { useSettings } from "../context/SettingsContext";
import PersonalNoteModal from "../components/PersonalNoteModal";
import { useBilling } from "../context/BillingContext";
import { Plus, Trash2, Save, Sparkles } from "lucide-react";

const EMPTY = { name:"", role:"", email:"", birthday:"", life_goals:"", purpose:"", hobbies:"", preferences:"", notes:"" };

export default function Team() {
  const { label } = useSettings();
  const { handleApiError } = useBilling();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [notePerson, setNotePerson] = useState(null);

  const load = async () => { const { data } = await api.get("/team"); setItems(data); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing.id) await api.put(`/team/${editing.id}`, editing);
      else await api.post("/team", editing);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { if (!handleApiError(e)) toast.error("Save failed"); }
  };
  const remove = async (id) => { await api.delete(`/team/${id}`); load(); };

  const inp = "w-full px-3 py-2.5 border border-zinc-300 focus:outline-none focus:border-[#0033FF] text-sm";
  const ta = inp + " min-h-[80px] leading-relaxed";

  return (
    <AppShell eyebrow="People" title={`${label("team")} Database`}
      actions={
        <button onClick={()=>setEditing({ ...EMPTY })} data-testid="team-new-btn"
          className="bg-[#0033FF] text-white px-5 py-3 text-xs font-bold uppercase tracking-wider hover:bg-zinc-900 inline-flex items-center gap-2">
          <Plus className="w-3.5 h-3.5" /> Add {label("team").toLowerCase().endsWith("s") ? label("team").toLowerCase().slice(0,-1) : "member"}
        </button>
      }>
      <div className="border border-zinc-200">
        {items.length === 0 && <div className="p-8 text-sm text-zinc-500" data-testid="team-empty">No team members yet. Add the first one — small notes today, thoughtful gestures tomorrow.</div>}
        {items.map((m) => (
          <div key={m.id} className="border-b border-zinc-200 last:border-0 p-4 flex items-start justify-between gap-4 hover:bg-zinc-50 group" data-testid={`team-item-${m.id}`}>
            <button onClick={()=>setEditing({ ...EMPTY, ...m })} className="text-left flex-1 min-w-0">
              <div className="text-lg font-bold tracking-tight">{m.name}</div>
              <div className="text-xs tracking-wider uppercase text-zinc-500 mt-0.5">{m.role || "—"} {m.birthday && `· 🎂 ${m.birthday}`}</div>
              {m.life_goals && <div className="text-sm text-zinc-700 mt-1 line-clamp-1"><span className="font-semibold">Goals:</span> {m.life_goals}</div>}
              {m.hobbies && <div className="text-sm text-zinc-700 line-clamp-1"><span className="font-semibold">Hobbies:</span> {m.hobbies}</div>}
            </button>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={()=>setNotePerson(m)} data-testid={`team-compose-${m.id}`} title="Compose personalized note"
                className="border border-zinc-300 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider hover:bg-[#0033FF] hover:text-white hover:border-[#0033FF] inline-flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Note
              </button>
              <button onClick={()=>remove(m.id)} data-testid={`team-delete-${m.id}`} className="text-zinc-400 hover:text-[#FF3B30] opacity-0 group-hover:opacity-100 p-1">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <PersonalNoteModal open={!!notePerson} personKind="team" person={notePerson} onClose={()=>setNotePerson(null)} />

      {editing && (
        <div className="fixed inset-0 bg-zinc-900/60 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={()=>setEditing(null)}>
          <div className="bg-white border border-zinc-200 w-full max-w-2xl p-6" onClick={(e)=>e.stopPropagation()} data-testid="team-edit-modal">
            <div className="text-xs tracking-[0.25em] uppercase text-[#0033FF] font-bold">Team member</div>
            <h3 className="text-2xl font-black tracking-tighter mt-1 mb-5">{editing.id ? "Edit" : "Add"} member</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <input data-testid="team-name" className={inp} placeholder="Name" value={editing.name} onChange={(e)=>setEditing({...editing, name: e.target.value})}/>
              <input data-testid="team-role" className={inp} placeholder="Role" value={editing.role} onChange={(e)=>setEditing({...editing, role: e.target.value})}/>
              <input data-testid="team-email" className={inp} placeholder="Email" value={editing.email} onChange={(e)=>setEditing({...editing, email: e.target.value})}/>
              <input data-testid="team-birthday" className={inp} placeholder="Birthday (e.g. Aug 12)" value={editing.birthday} onChange={(e)=>setEditing({...editing, birthday: e.target.value})}/>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <textarea data-testid="team-goals" className={ta} placeholder="Life goals" value={editing.life_goals} onChange={(e)=>setEditing({...editing, life_goals: e.target.value})}/>
              <textarea data-testid="team-purpose" className={ta} placeholder="Purpose / what drives them" value={editing.purpose} onChange={(e)=>setEditing({...editing, purpose: e.target.value})}/>
              <textarea data-testid="team-hobbies" className={ta} placeholder="Hobbies" value={editing.hobbies} onChange={(e)=>setEditing({...editing, hobbies: e.target.value})}/>
              <textarea data-testid="team-prefs" className={ta} placeholder="Preferences (coffee, gifts, comm style)" value={editing.preferences} onChange={(e)=>setEditing({...editing, preferences: e.target.value})}/>
            </div>
            <textarea data-testid="team-notes" className={ta} placeholder="Other notes" value={editing.notes} onChange={(e)=>setEditing({...editing, notes: e.target.value})}/>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={()=>setEditing(null)} className="border border-zinc-300 px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-zinc-100">Cancel</button>
              <button onClick={save} data-testid="team-save" className="bg-[#0033FF] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-zinc-900 inline-flex items-center gap-2">
                <Save className="w-3.5 h-3.5" /> Save
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
